import AddCompanyForm, { type CreatedCompany } from '@/components/features/Companies/forms/AddCompanyForm';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { useTranslations } from '@/hooks/useTranslations';
import { Building2 } from 'lucide-react';

interface Company {
    id: string;
    name: string;
    company_code: string;
    country: string;
}

interface CreateCompanyModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess?: (company: Company) => void;
    /** Registry code to pre-fill the form with — set when the user picked a company that is not in the CRM yet. */
    prefillRegistryCode?: string | null;
}

export default function CreateCompanyModal({ isOpen, onClose, onSuccess, prefillRegistryCode = null }: CreateCompanyModalProps) {
    const { t } = useTranslations();

    const handleCompanyCreated = (company: CreatedCompany) => {
        if (onSuccess) {
            onSuccess(company);
        }
        onClose();
    };

    const handleCancel = () => {
        onClose();
    };

    return (
        <ResponsiveModal open={isOpen} onOpenChange={onClose} variant="sheet">
            {/* The full create form, same as /companies/create — a company raised
                from a picker should not be a lesser record than one created there. */}
            <ResponsiveModalContent size="xl">
                <ResponsiveModalHeader>
                    <ResponsiveModalTitle className="flex items-center gap-2">
                        <Building2 className="h-5 w-5 shrink-0" />
                        {t('companies.forms.create_modal.title', 'Create New Company')}
                    </ResponsiveModalTitle>
                    <ResponsiveModalDescription>
                        {prefillRegistryCode
                            ? t(
                                  'companies.forms.create_modal.description_registry',
                                  'Pre-filled from the state registry. Complete the remaining fields and save.',
                              )
                            : t('companies.forms.create_modal.description', 'Add a new company to the system. Required fields are marked with *.')}
                    </ResponsiveModalDescription>
                </ResponsiveModalHeader>

                {/* AddCompanyForm supplies its own cancel/create buttons. */}
                <ResponsiveModalBody>
                    {/* Keyed on the code so reopening for a different registry hit starts from a clean form. */}
                    <AddCompanyForm
                        key={prefillRegistryCode ?? 'blank'}
                        onCompanyCreated={handleCompanyCreated}
                        onCancel={handleCancel}
                        prefillRegistryCode={prefillRegistryCode}
                    />
                </ResponsiveModalBody>
            </ResponsiveModalContent>
        </ResponsiveModal>
    );
}
